import {ActivatedRoute} from '@angular/router';
import {HttpParams} from '@angular/common/http';
import {take} from 'rxjs/operators';
import animateScrollTo from 'animated-scroll-to';
import _ from 'lodash';
// import * as _ from 'lodash';

import {SearchParam} from './client.const';
import {GUKEO_API_URL1} from './protocol';
import {isRegion2, JASO, REGION2, REGION2_DEFAULT} from './gukeo.const';
import {IsEmptyString} from './common.func';
import {SaseungHanja} from "../models/saseung.model";
import {NormalizeSearchKeyword} from './search-keyword.func';


export function consoleEl(el: HTMLElement): void {
  console.warn("consoleEl", el.id, el, `scrollTop:${el.scrollTop}, scrollHeight:${el.scrollHeight}, \
offsetTop:${el.offsetTop}, offsetHeight:${el.offsetHeight}, \
clientTop:${el.clientTop}, clientHeight:${el.clientHeight}`, el.offsetParent);
}


/**
 * HTMLElement에서 style값을 가지고 오기
 * @param oElm - HTMLElement (type library로 지정하면 오류난다)
 * @param strCssRule - C프로퍼티 이름
 */
export function getStyle(oElm, strCssRule: string): string {
  let strValue = '';
  if (document.defaultView && document.defaultView.getComputedStyle){
    strValue = document.defaultView.getComputedStyle(oElm, '').getPropertyValue(strCssRule);
  }
  else if (oElm.currentStyle){
    strCssRule = strCssRule.replace(/\-(\w)/g, (strMatch, p1) => p1.toUpperCase());
    strValue = oElm.currentStyle[strCssRule];
  }
  return strValue;
}


/**
 * 대상 객체의 padding-top 속성을 얻는다
 * @param el
 */
export function getPaddingTop(el: Node): number|null {
  const txtPaddingTop = getStyle(el, 'padding-top');
  if (txtPaddingTop.includes('px')) {
    const paddingTop = parseInt(txtPaddingTop, 10);
    // console.log('getPaddingTop()', paddingTop);
    return paddingTop;
  }
  else {
    return null;
  }
}

/**
 * 대상 객체에서 기준 오브젝트까지의 offsetTop을 계산한다.
 * @param el 대상 객체
 * @param until 기준 객체
 */
export function getOffsetTop(el: HTMLElement, until: HTMLElement): number {
  let offsetTop = 0;

  while (el && el !== until) {
    // console.log('offsetParent =', el);
    offsetTop += el.offsetTop;

    el = el.offsetParent as HTMLElement;
  }
  // console.log('all offsetTop = ', offsetTop);
  return offsetTop;
}

/**
 * 화면을 지정한 요소가 위로 보이도록 스크롤 시킨다.
 * @param elContainer 스크롤 대상
 * @param el 잘 보이도록 하는 대상
 */
export function scrollToNode(elContainer: HTMLElement, el: Node, delta: number = 0): number {
  const paddingTop = getPaddingTop(elContainer);

  if (paddingTop !== null) {
    /*********************************************************************
     * 선택된 아이템을 상단으로 스크롤 시킨다.
     */
    // const scrollTop = el.offsetTop - paddingTop;  // 스크롤 할 값은 padding 값을 때야 함. 1-depth일때 가능한 코드
    const offsetTop = getOffsetTop(el as HTMLElement, elContainer);
    const scrollTop = offsetTop - paddingTop;  // 스크롤 할 값은 padding 값을 때야 함.
    // console.log(`scrollTop:${scrollTop} paddingTop:${paddingTop}`);
    // elContainer.scrollTop = scrollTop;
    const oldScrollTop = elContainer.scrollTop;

    // WARN: 여기서 스크롤을 바로 호출하면 아직 padding-bottom값이 설정 안됐기때문에 스크롤 되지 않는다.
    // 그래서 시간차이를 두고 padding 값이 반영되도록 한 뒤에 호출한다.
    // elContainer.scrollTo({top: scrollTop, behavior: 'smooth'});
    animateScrollTo(scrollTop, {
      elementToScroll: elContainer,
      speed: 1000,
      maxDuration: 100,
      easing: t => 1 - (--t) * t * t * t,   // easeOutQuart
      cancelOnUserAction: false,
      verticalOffset: delta,
    }).then();

    return oldScrollTop;
  }
  // container 에 설정한 padding-top 이 px 단위가 아니면 계산이 안 맞는다.
  throw Error('container: padding-top does not px unit!');
}

/**
 * 상세 팝업창의 크기를 계산한다.
 * @param elContainer 스크롤 되는 대상
 * @param el 이거 밑으로 팝업창이 붙도록 크기를 계산한다.
 * @param delta 보정값, 기본으로 footer의 높이를 넣는다.
 */
export function getDetailHeight(elContainer: HTMLElement, el: Node, delta: number): number {
  const paddingTop = getPaddingTop(elContainer);
  const elChildShow = el.firstChild as HTMLElement; // 상세 팝업이 이 영역 밑으로 붙어야 한다.

  // consoleEl(elChildShow);
  const childPaddingTop = getPaddingTop(el) ?? 0;
  const detailHeight = window.innerHeight - paddingTop
    - elChildShow.offsetHeight - childPaddingTop  // elItemHeader에서 표시되는 영역의 크기.
    - delta;                                      // 너무 바짝 붙이면 보기가 싫어서 조금 간격을 띄움.

  // console.warn('getDetailHeight()', detailHeight, window.innerHeight, paddingTop,
  //   elChildShow.offsetHeight, childPaddingTop, delta, detailHeight);
  return Math.max(detailHeight, 0);
}


/**
 * 문자열에 https:// 나 http:// 가 포함되었는지 확인하는 함수
 */
export function IsContainHttp(value: string): boolean {
  return /(http(s?)):\/\//i.test(value);
}


/**
 * HTMLElement에서 padding을 제외한 안쪽 영역의 크기를 가져오는 함수
 * @param element
 */
export function getContentSize(element: HTMLElement): { width: number, height: number } {
  const style = window.getComputedStyle(element);

  const paddingTop = parseInt(style.paddingTop, 10);
  const paddingRight = parseInt(style.paddingRight, 10);
  const paddingBottom = parseInt(style.paddingBottom, 10);
  const paddingLeft = parseInt(style.paddingLeft, 10);
  // console.warn('getContentSize()', element.offsetWidth, element.offsetHeight, paddingLeft, paddingRight, paddingTop, paddingBottom);

  const width = element.offsetWidth - paddingLeft - paddingRight;
  const height = element.offsetHeight - paddingTop - paddingBottom;
  // console.warn('getContentSize()', width, height);

  return { width, height };
}


export function makeImageUrl(image?: string): string {
  if (image) {
    return IsContainHttp(image) ? image : GUKEO_API_URL1 + image;
  }
  else {
    return 'assets/img/img_empty_video.png';
  }
}

export function makeYoutubeUrl(youtubeId: string): string {
  return `https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`;
}

/**
 * QueryParam 에서 SearchParam 및 기타 초기화 값을 얻는다.
 * @param param
 * @param jaso
 * @returns
 */
export function GetSearchFromQuery(param: SearchParam, jaso: string | null = null): [SearchParam, JASO | null, string, REGION2, SearchParam] {
  let searchParam: SearchParam = IsEmptyString(jaso) ? {} : { index: jaso };
  let jasoIdx: JASO | null = IsEmptyString(jaso) ? null : jaso;
  let keyword: string = null;
  let region2: REGION2 = REGION2_DEFAULT;

  if (param) {
    const keywordParam = NormalizeSearchKeyword(param.keyword);
    if (!IsEmptyString(keywordParam)) {
      searchParam = {...param, keyword: keywordParam};
      jasoIdx = null;
      keyword = keywordParam;
    } else if (param?.index) {
      jasoIdx = param.index;
      searchParam.index = param.index;
    }

    if (param?.region2 && isRegion2(param.region2)) {
      region2 = param.region2;

      if (region2 !== REGION2_DEFAULT) {
        searchParam.region2 = param.region2;
      }
      else {
        delete searchParam.region2;
      }
      delete searchParam.start;
    }

    if(param?.start){
      searchParam.start = param.start;
    }
  }
  // console.log(`GetSearchFromQuery() keyword=${keyword}`, param, searchParam, jasoIdx, region);
  // console.warn(`GetSearchFromQuery() keyword=${keyword}, jasoIdx=${jasoIdx}, region2=${region2}`, ' param=', param, ' searchParam=', searchParam);
  return [searchParam, jasoIdx, keyword, region2, param];
}

/**
 *
 * @returns - 쿼리 파라미터
 */

export async function getQueryParams(route: ActivatedRoute): Promise<any> {
  return await route.queryParams.pipe(take(1)).toPromise().then(params => params);
}

/**
 * start값으로 해당 요소 구하기
 * @param prefix - 해당 요소 prefix : ex) WORD_SELECT_ID
 * @param start - 리스트에서 보여줄 첫단어
 * @returns - 엘리먼트
 */

export function getElementBystart(prefix: string, start: string) {
  return document.getElementById(prefix + start);
}

// Query Parameters를 입력 받아 URL을 만드는 함수
export function generateUrlWithQueryParams(basePath: string, params: { [key: string]: any }): string {
  const queryParams = new URLSearchParams();

  // 객체의 각 key-value 쌍을 순회
  for (const key of Object.keys(params)) {
    queryParams.set(key, params[key]);
  }

  // 전체 URL 만들기
  return `${basePath}?${queryParams.toString()}`;
}

/**
 * 쿼리파라미터를 Url 스트링값으로 변환
 * @param params - 쿼리파라미터 값
 * @returns - 쿼리파라미터 Url 스트링 변환값
 */
export function getSearchUrlFromQueryParams(params: any): string {
  return decodeURI(new HttpParams({ fromObject: params }).toString());
}

/**
 *  Search url을 쿼리파라미터로 변환
 * @param search
 * @returns
 */
export function getQueryParamsFromSearchUrl(search: string): Record<string, any> {
  search = decodeURI(search);
  return _.fromPairs(search?.split('&').map(s => s.split('=')));
}


export function GetHanjaHtml(hanja: string): string {
  let hanjaHtml = "";

  // 문자열에 UTF-8 MB4가 존재하므로 주의해서 처리해야 한다.
  Array.from(hanja).forEach((char: string, idx: number) => {
    if (idx === 0) {
      hanjaHtml = char;
    } else if (idx === 1) {
      hanjaHtml += '<span class="hanja-size">' + char;
    } else if (idx % 2 === 0) {
      hanjaHtml += '<br/>' + char;
    } else {
      hanjaHtml += char;
    }
  });
  if (hanja.length > 1) {
    hanjaHtml += '</span>';
  }
  return hanjaHtml;
}


/**
 * 뜻에서 선택된 훈을 강조하는 함수.
 * 훈 사이에 공백이 일정하지 않을 수 있어서 번거롭게 처리함.
 *
 * @param meaning
 * @param hun
 * @constructor
 */
export function SetHunHightlight(meaning: string, hun: string): string {
  if (IsEmptyString(hun)) {
    return meaning;
  }

  const huns: string[] = meaning.split('|')
  const huns2 = huns.map(hun1 => {
    if (hun == hun1.trim()) {
      return hun1.replace(hun, `<span class="highlight">${hun}</span>`);
    }
    return hun1;
  });

  return huns2.join('|');
}
